import { useState } from 'react';
import { agentSharing } from './agent-sharing';
import { Card } from './components';

export function AgentSharing({
  agent,
}: {
  agent: { publicId: string; isActive: boolean; allowedOrigins: Array<{ origin: string }> };
}) {
  const [inline, setInline] = useState(false);
  const [message, setMessage] = useState('');
  const origin = window.location.origin;
  const demoBaseUrl =
    typeof import.meta.env.VITE_DEMO_BASE_URL === 'string'
      ? import.meta.env.VITE_DEMO_BASE_URL
      : undefined;
  const { demoUrl, snippet } = agentSharing(
    agent.publicId,
    origin,
    String(import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000'),
    inline,
    demoBaseUrl,
  );
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setMessage('Copied to clipboard.');
    } catch {
      setMessage('Clipboard unavailable. Select and copy the link or code below manually.');
    }
  };
  return (
    <Card>
      <h2>Share & embed</h2>
      <p>
        Visitors do not need an admin account. Anyone with the demo link can share it; use approved
        or fictional knowledge only.
      </p>
      {!agent.isActive && <p>Save this agent as active before sharing the demo.</p>}
      {!agent.allowedOrigins.some((item) => item.origin === new URL(demoUrl).origin) && (
        <p>
          Add {new URL(demoUrl).origin} to this agent’s allowed website origins for the demo. Add
          the client website origin separately for embedding.
        </p>
      )}
      <label>
        Public demo link
        <input readOnly value={demoUrl} onFocus={(event) => event.target.select()} />
      </label>
      <label>
        Embed display
        <select
          value={inline ? 'inline' : 'floating'}
          onChange={(event) => setInline(event.target.value === 'inline')}
        >
          <option value="floating">Floating chat button</option>
          <option value="inline">Inline chat section</option>
        </select>
      </label>
      <div className="share-actions">
        <a className="button" href={demoUrl} target="_blank" rel="noopener noreferrer">
          Open demo
        </a>
        <button type="button" onClick={() => void copy(demoUrl)}>
          Copy demo link
        </button>
        <button type="button" onClick={() => void copy(snippet)}>
          Copy embed code
        </button>
      </div>
      <label>
        Embed code
        <textarea
          className="share-code"
          readOnly
          rows={10}
          value={snippet}
          onFocus={(event) => event.target.select()}
        />
      </label>
      <p role="status">{message}</p>
      <small>
        Paste before &lt;/body&gt;. Inline mode also needs the included div. Public IDs are not
        passwords. Knowledge must be READY; changes affect all websites using this agent.
      </small>
    </Card>
  );
}
